import React, { useState } from "react";
import { api } from "@/lib/api";
import { X, Sparkles, Briefcase, Camera, Folder, Package, Heart, Plane, BookOpen, Wrench, Users } from "lucide-react";

const ICONS = [
  { name: "Folder", Cmp: Folder },
  { name: "Camera", Cmp: Camera },
  { name: "Briefcase", Cmp: Briefcase },
  { name: "Sparkles", Cmp: Sparkles },
  { name: "Package", Cmp: Package },
  { name: "Heart", Cmp: Heart },
  { name: "Plane", Cmp: Plane },
  { name: "BookOpen", Cmp: BookOpen },
  { name: "Wrench", Cmp: Wrench },
  { name: "Users", Cmp: Users },
];

export default function NewTabModal({ open, onClose, onCreated }) {
  const [type, setType] = useState("tracker");
  const [label, setLabel] = useState("");
  const [subject, setSubject] = useState("Project");
  const [icon, setIcon] = useState("Folder");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const create = async (e) => {
    e?.preventDefault();
    if (!label.trim()) return;
    setBusy(true);
    try {
      const payload = {
        type,
        label: label.trim(),
        icon,
        subject_label: type === "tracker" ? subject.trim() || "Project" : undefined,
        services: type === "tracker"
          ? [{ name: "Service A", price_cents: 10000 }, { name: "Service B", price_cents: 5000 }]
          : [],
      };
      const t = await api.post("/tabs", payload).then((r) => r.data);
      onCreated?.(t);
      setLabel("");
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/65 backdrop-blur-sm flex items-start justify-center pt-16 px-4 animate-fadein"
      onClick={onClose}
      data-testid="new-tab-modal"
    >
      <form
        onSubmit={create}
        className="hud-frame w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        style={{ background: "hsl(var(--popover))" }}
      >
        <div className="tread-plate h-1.5" />
        <div className="p-6 space-y-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="kpi-label">Add Tab</div>
              <h2 className="display text-xl font-bold uppercase mt-1">New Section</h2>
            </div>
            <button type="button" onClick={onClose} className="hud-btn !px-2 !py-1">
              <X size={11} />
            </button>
          </div>

          <div>
            <div className="kpi-label">Template</div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button
                type="button"
                onClick={() => setType("tracker")}
                data-active={type === "tracker"}
                className={`hud-btn !py-3 flex flex-col items-start ${type === "tracker" ? "border-hud" : ""}`}
                data-testid="nt-tracker"
              >
                <span className="font-semibold normal-case text-sm">Tracker</span>
                <span className="text-[10px] text-muted-foreground normal-case mt-0.5">
                  Services + prices + totals
                </span>
              </button>
              <button
                type="button"
                onClick={() => setType("list")}
                data-active={type === "list"}
                className={`hud-btn !py-3 flex flex-col items-start ${type === "list" ? "border-hud" : ""}`}
                data-testid="nt-list"
              >
                <span className="font-semibold normal-case text-sm">List</span>
                <span className="text-[10px] text-muted-foreground normal-case mt-0.5">
                  Items, dates, notes
                </span>
              </button>
            </div>
          </div>

          <div>
            <div className="kpi-label">Tab Name</div>
            <input
              autoFocus
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="hud-input mt-2"
              placeholder={type === "tracker" ? "Coaching, Photography, Consulting…" : "Inventory, Wishlist, Contacts…"}
              required
              data-testid="nt-label"
            />
          </div>

          {type === "tracker" && (
            <div>
              <div className="kpi-label">Subject Column</div>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="hud-input mt-2"
                placeholder="What does each row represent?"
                data-testid="nt-subject"
              />
            </div>
          )}

          <div>
            <div className="kpi-label">Icon</div>
            <div className="grid grid-cols-5 gap-2 mt-2">
              {ICONS.map(({ name, Cmp }) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setIcon(name)}
                  className={`hud-btn !py-2 !px-0 justify-center ${icon === name ? "border-hud bg-[hsl(var(--oxblood)/0.15)]" : ""}`}
                  data-testid={`nt-icon-${name}`}
                >
                  <Cmp size={14} />
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
            <button type="button" onClick={onClose} className="hud-btn">CANCEL</button>
            <button
              type="submit"
              disabled={busy || !label.trim()}
              className="hud-btn hud-btn-primary"
              data-testid="nt-create"
            >
              {busy ? "CREATING…" : "CREATE TAB"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
